import {
  type ColumnFragment,
  type ControlCardComboBoxOptionFragment,
  ControlCardComboBoxOptionsDocument,
  type ValueFragment,
  type ValueInstanceFragment
} from "app/gql/graphqlSchema";
import {IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon} from "@ionic/react";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {RhfIonInput} from "app/employee/rhf/RhfIonInput";
import {z} from "zod";
import {isNil, isNotBlank, normalizeToNull} from "app/utils/stdlib";
import {zodResolver} from "@hookform/resolvers/zod";
import {EMPTY_VALUE} from "app/employee/controlCard/types";
import {IonCardFooter} from "app/employee/controlCard/ControlCardUI";
import {checkmarkCircleOutline, chevronForward, closeOutline, refreshCircleOutline} from "ionicons/icons";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {SavingProgress} from "app/employee/controlCard/input/SavingProgress";
import {useExpressionValue} from "app/employee/controlCard/hook/useExpressionValue";
import {EditContainer} from "app/employee/incdoc/EditContainer";
import {transformToApplicationError} from "app/gql/handleGraphqlErrors";
import {setApplicationError} from "app/utils/parseErrorResult";
import {DisplayRootError} from "app/employee/controlCard/input/DisplayRootError";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";
import {useEditComboBoxOption} from "app/employee/controlCard/useEditComboBoxOption";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import type {MaybeNil} from "app/utils/types";
import {IdSingleComboBox} from "app/employee/controlCard/IdSingleComboBox";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardComboBoxColumn"};
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

const formSchema = z.object({
  value: z.string().nullable().transform(normalizeToNull)
});

export type FormData = z.infer<typeof formSchema>;

const isOptionEqualToValue = (option: ControlCardComboBoxOptionFragment, value: string) => {
  return option.value === value;
};

export const InputComboBox = ({column, valueInstance, disabled}: Props) => {
  const {t} = useTranslation();
  const {isComplete, canEdit} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);
  const comboBoxRef = useRef<HTMLIonModalElement | null>(null);

  const {persistedValue, convertToComputed, showRestoreExpression, saving, saveValue} = useExpressionValue(
    column,
    valueInstance
  );

  let value: string | null = null;
  if (persistedValue.__typename === "ControlCardTextValue") {
    value = persistedValue.text;
  }

  const {
    watch,
    control,
    formState: {isDirty, isSubmitting, disabled: formDisabled, errors},
    setValue,
    handleSubmit,
    reset,
    setError
  } = useForm<FormData>({
    values: {
      value
    },
    disabled: isComplete || !canEdit || !!disabled,
    resolver: zodResolver(formSchema)
  });

  const handleSave = useMemo(
    () =>
      handleSubmit(async (data) => {
        try {
          let value: ValueFragment;
          if (isNil(data.value)) {
            value = EMPTY_VALUE;
          } else {
            value = {__typename: "ControlCardTextValue", text: data.value};
          }

          await saveValue(value);
        } catch (e) {
          const appError = transformToApplicationError(e);
          setApplicationError(appError, setError);
        }
      }),
    [handleSubmit, saveValue, setError]
  );

  const handleClear = () => reset();

  useEffect(() => {
    if (isComplete) {
      reset();
    }
  }, [isComplete, reset]);

  const handleSelect = useCallback(
    async (value: MaybeNil<ControlCardComboBoxOptionFragment>) => {
      setValue("value", value?.value ?? null, {shouldDirty: true, shouldValidate: true});
      await Promise.allSettled([handleSave(), comboBoxRef.current?.dismiss()]);
    },
    [handleSave, setValue]
  );

  const {addItem, editItem, textExtractor, keyExtractor} = useEditComboBoxOption(column.listKey);
  const {data: optionsData, refetch, fetch} = useQuery(ControlCardComboBoxOptionsDocument);

  const currentValue = watch("value");

  return (
    <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
      <EditContainer>
        {showRestoreExpression && (
          <IonButton disabled={formDisabled} fill="clear" onClick={convertToComputed}>
            <IonIcon icon={refreshCircleOutline} slot="icon-only" />
          </IonButton>
        )}
      </EditContainer>
      <IonCardHeader>
        <IonCardTitle>{column.title}</IonCardTitle>
        {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
      </IonCardHeader>

      <IonCardContent>
        <DisplayRootError errors={errors} />
        <RhfIonInput
          fill="outline"
          label={column.title}
          labelPlacement="floating"
          onClick={!formDisabled ? async () => await comboBoxRef.current?.present() : undefined}
          readonly
          rhf={{control, name: "value"}}
        >
          <IonIcon icon={chevronForward} slot="end" />
        </RhfIonInput>
        <DisplayValueInstanceError column={column} disabled={formDisabled} valueInstance={valueInstance} />
      </IonCardContent>
      {isDirty && !isSubmitting && (
        <IonCardFooter>
          <IonButton color="primary" fill="outline" onClick={handleClear} size="small">
            <IonIcon icon={closeOutline} slot="start" />
            {t("g.cancel")}
          </IonButton>
          <IonButton color="primary" disabled={formDisabled} onClick={handleSave} size="small">
            <IonIcon icon={checkmarkCircleOutline} slot="start" />
            {t("g.save")}
          </IonButton>
        </IonCardFooter>
      )}
      <SavingProgress isSubmitting={saving} />
      <FstIonModal onIonModalWillPresent={() => fetch({input: {listKey: column.listKey}})} ref={comboBoxRef}>
        <IdSingleComboBox
          isEqual={isOptionEqualToValue}
          keyExtractor={keyExtractor}
          onCreate={addItem}
          onEdit={editItem}
          onSelect={handleSelect}
          options={optionsData.value?.controlCardComboBoxOptions}
          refetch={refetch}
          textExtractor={textExtractor}
          title={column.title}
          value={currentValue}
        />
      </FstIonModal>
    </InputIonCard>
  );
};
